<template>
  <div class="main-info">
    <CartCarousel />
    <div class="carts-count">
      <div class="count-wrapper">
        Cart
        <div class="count">
          {{ cartCount > 99 ? '99+' : cartCount }}
        </div>
      </div>
      <div class="batch-select">
        <div
          v-if="!isEmpty"
          @click="handleBatchSelect"
          class="select-all"
        >
          <img
            v-if="checkedTabIndex === 3 || isLoading"
            src="@/assets/H5/carts/disabled-check.svg"
            alt=""
          >
          <template v-else>
            <img
              v-if="isBatchSelect"
              src="@/assets/order/cart-checked.svg"
              alt=""
            >
            <img
              v-else
              src="@/assets/order/cart-uncheck.svg"
              alt=""
            >
          </template>
          Select all items
        </div>
        <div
          v-if="!isEmpty && hasChecked"
          @click="handleBatchDelete"
          class="delete-seleted"
        >
          <div class="line" />
          <div class="text">
            Delete Selected items
          </div>
        </div>
      </div>
    </div>
    <div
      class="cart-tab-wrapper"
      v-if="!(isEmpty && checkedTabIndex === 0)"
    >
      <div
        v-for="(tabName, index) in tabNameList"
        :key="index"
        class="cart-tab-item"
        :class="{ active: checkedTabIndex === index }"
        @click="handleCheckedTab(index, tabName)"
      >
        {{ tabName }}
      </div>
    </div>
    <div
      v-if="!isEmpty"
      style="flex: 1;"
      class="cart-list"
    >
      <slot />
    </div>
    <!-- 空状态 -->
    <div v-else>
      <div
        v-if="checkedTabIndex === 0"
        class="empty-cart"
      >
        <img
          src="@/assets/order/empty-cart.svg"
          width="200px"
          alt=""
        >
        <div class="text">
          <div class="text-1">
            Your shopping cart is empty
          </div>
          <div class="text-2">
            Add your purchase items in it.
          </div>
        </div>
        <div
          class="button"
          @click="$router.push('/')"
        >
          Back Home
        </div>
      </div>
      <div
        v-else
        class="empty-tab"
      >
        <img
          src="@/assets/illus/empty-tab.svg"
          width="156px"
          alt=""
        >
        It is empty here :-(
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { usePlugin } from '@/hooks/usePlugin'
import { fetchData } from '@/utils/fetch'
import CartCarousel from '../../CartCarousel/index.vue'

const props = defineProps({
  isEmpty: {
    type: Boolean,
    default: false
  },
  isLoading: {
    type: Boolean,
    default: false
  },
  list: {
    type: Array,
    default: () => []
  }
})

const emits = defineEmits(['batchSelect', 'fetchList', 'tabChange'])

const { $confirm, $toast } = usePlugin()

const store = useStore()

const isBatchSelect = ref(true)

const hasChecked = ref(true)

const tabNameList = ref(['All', 'Available', 'Processing', 'Unavailable'])

const checkedTabIndex = ref(0)

const cartCount = computed(() => store.state.cart.cartCount)

watch(() => props.list, (value) => {
  if (value && value.length) {
    const allChecked = value.filter(item => [0, 10].includes(item.cartStatus)).every(item => item.checked)
    hasChecked.value = value.some(item => item.checked)
    isBatchSelect.value = allChecked
  }
}, { deep: true })

const handleBatchSelect = () => {
  if (checkedTabIndex.value === 3) return
  isBatchSelect.value = !isBatchSelect.value
  emits('batchSelect', isBatchSelect.value)
}

const handleBatchDelete = () => {
  const selectedList = props.list.filter(item => item.checked === true)
  const cartIdList = selectedList.map(item => item.cartId)

  $confirm('Delete item?', 'Are you sure you want to delete itme from cart?').then(async () => {
    const { code } = await fetchData('/appb/b2b/cart/cartDelete', {
      method: 'post',
      body: JSON.stringify({
        cartIds: cartIdList
      })
    })
    if (code === 200) {
      $toast('Deleted successfully.', { type: 'success' })
      emits('fetchList')
    }
  })
}

const handleCheckedTab = (index, tabName) => {
  checkedTabIndex.value = index

  const statusMap = {
    All: null,
    Available: '2',
    Processing: '0',
    Unavailable: '1'
  }
  emits('tabChange', statusMap[tabName])
}
</script>

<style lang='less' scoped>
.main-info {
  display: flex;
  flex-direction: column;
  .carts-count {
    display: flex;
    justify-content: space-between;
    width: var(--cart-width);
    padding-bottom: 16px;
    border-bottom: 1px solid var(--light-split-color);
    margin-top: 24px;
    .count-wrapper {
      color: #232323;
      font-size: 20px;
      font-family: mon-700;
      display: flex;
      .count {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 24px;
        height: 24px;
        border-radius: 50%;
        margin-left: 8px;
        background-color: #009de0;
        color: white;
        font-size: 12px;
        font-family: mon-600;
      }
    }

    .batch-select {
      display: flex;
      align-items: center;

      .select-all {
        display: flex;
        gap: 8px;
        align-items: center;
        color: #232323;
        font-size: 12px;
        font-family: mon-500;
        cursor: pointer;
        img {
          width: 20px;
          height: 20px;
        }
      }
      .delete-seleted {
        display: flex;
        align-items: center;
        .line {
          height: 12px;
          width: 1px;
          background-color: var(--light-split-color);
          margin: 0 12px;
        }
        .text {
          color: #5c5c5c;
          font-size: 12px;
          font-family: mon-500;
          &:hover {
            color: var(--link-hover-color);
            text-decoration: underline;
            cursor: pointer;
          }
        }
      }
    }
  }

  .cart-tab-wrapper {
    display: flex;
    align-items: center;
    gap: 12px;
    width: var(--cart-width);
    margin-top: 20px;

    .cart-tab-item {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 40px;
      padding: 11px 16px;
      border-radius: 20px;
      background-color: #f8f8f8;
      color: #5c5c5c;
      font-size: 12px;
      font-family: mon-600;
      cursor: pointer;

      &.active {
        color: white;
        background-color: #232323;
      }
    }
  }

  .empty-cart {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: calc(100vh - 220px - 128px - 48px);
    width: var(--cart-width);
    margin-top: 40px;
    .text {
      text-align: center;
      .text-1 {
        color: #232323;
        font-size: 16px;
        font-family: mon-600;
        line-height: 24px;
      }
      .text-2 {
        color: #232323;
        font-size: 14px;
        font-family: mon-500;
        line-height: 18px;
      }
    }
    .button {
      border: 1px solid #b2b7c2;
      border-radius: 20px;
      width: 162px;
      height: 40px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #232323;
      font-size: 14px;
      font-family: mon-600;
      margin-top: 16px;
      cursor: pointer;
    }
  }

  .empty-tab {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 36px;
    align-items: center;
    color: #232323;
    font-size: 14px;
    font-family: mon-600;
    height: calc(100vh - 220px - 128px - 48px);
  }
}
</style>
